import { useState, useEffect } from 'react';

interface NetworkConnection extends EventTarget {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  saveData?: boolean;
  downlink?: number;
  rtt?: number;
}

interface Navigator {
  connection?: NetworkConnection;
  mozConnection?: NetworkConnection;
  webkitConnection?: NetworkConnection;
}

interface NetworkStatus {
  isOnline: boolean;
  effectiveType: '4g' | '3g' | '2g' | 'slow-2g' | 'unknown';
  saveData: boolean;
  downlink: number; // Mbps
  rtt: number; // Round-trip time in ms
}

/**
 * Hook for monitoring network connection status
 * 
 * Uses the Navigator.connection API (where available) to get detailed
 * network information including connection type, speed, and data saver status.
 * 
 * @returns NetworkStatus object with connection details
 * 
 * @example
 * const networkStatus = useNetworkStatus();
 * 
 * if (networkStatus.saveData || networkStatus.effectiveType === '2g') {
 *   // Skip heavy prefetching
 * }
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [status, setStatus] = useState<NetworkStatus>(() => {
    const nav = navigator as Navigator;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    
    return {
      isOnline: navigator.onLine,
      effectiveType: connection?.effectiveType || 'unknown',
      saveData: connection?.saveData || false,
      downlink: connection?.downlink || 10,
      rtt: connection?.rtt || 50,
    };
  });

  useEffect(() => {
    const nav = navigator as Navigator;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

    const updateStatus = () => {
      setStatus({
        isOnline: navigator.onLine,
        effectiveType: connection?.effectiveType || 'unknown',
        saveData: connection?.saveData || false,
        downlink: connection?.downlink || 10,
        rtt: connection?.rtt || 50,
      });
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    connection?.addEventListener('change', updateStatus);

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
      connection?.removeEventListener('change', updateStatus);
    };
  }, []);

  return status;
};

/**
 * Helper to check if prefetching should be enabled
 * 
 * Returns false if:
 * - User is offline
 * - User has data saver enabled
 * - Connection is 2G or slower
 * 
 * @param status - NetworkStatus from useNetworkStatus hook
 * @returns boolean indicating if prefetching should proceed
 */
export const shouldPrefetch = (status: NetworkStatus): boolean => {
  if (!status.isOnline) return false;
  if (status.saveData) return false;
  if (status.effectiveType === '2g' || status.effectiveType === 'slow-2g') return false;
  
  return true;
};

/**
 * Get prefetch tier based on network quality
 * 
 * Returns:
 * - 0: No prefetch (offline, 2G, or data saver)
 * - 1: Tier 1 only (3G - critical sections only)
 * - 2: Tier 1+2 (slow 4G - critical + medium priority)
 * - 3: All tiers (fast 4G - full prefetch)
 * 
 * @param status - NetworkStatus from useNetworkStatus hook
 * @returns Tier level (0-3)
 */
export const getPrefetchTier = (status: NetworkStatus): 0 | 1 | 2 | 3 => {
  if (!status.isOnline || status.saveData) return 0;
  if (status.effectiveType === '2g' || status.effectiveType === 'slow-2g') return 0;
  if (status.effectiveType === '3g') return 1;
  if (status.downlink < 2) return 2;
  return 3;
};
